/*
  Fahrzeugsuche für Reifen.

  Zwei Einstiege, ein Ergebnis: die Kaskade über fünf Auswahlfelder und die
  Schweizer Typenscheinnummer.

  Die Datenbeschaffung liegt vollständig in `Quelle`. Solange keine Adresse
  hinterlegt ist, antwortet der Demobestand am Dateiende; sobald eine steht,
  gehen dieselben Aufrufe an die Schnittstelle. Ausserhalb von `Quelle` weiss
  niemand, woher die Daten kommen — das ist der Punkt.

  Erwartete Schnittstelle:

    GET  {basis}/marken
    GET  {basis}/modelle?marke=VW
    GET  {basis}/typen?marke=VW&modell=Golf
    GET  {basis}/karosserien?marke=VW&modell=Golf&typ=5G
    GET  {basis}/motoren?marke=VW&modell=Golf&typ=5G&karosserie=Kombi
    GET  {basis}/fahrzeug?...            -> Fahrzeug samt Reifensätzen
    GET  {basis}/typenschein/1OC253      -> dasselbe über die Nummer

  Listenantworten sind Zeichenketten oder Objekte mit `wert` und `text`.

  Für `typen` bitte immer Objekte liefern, mit dem Typcode als `wert` und dem
  Baujahr davor im `text`:

      { "wert": "5G", "text": "2012–2020 · Typ 5G" }

  Der Code ist unser Schlüssel, das Baujahr ist das, was der Kunde kennt.
*/

/*
  Alles ab hier steht in einem Block, und der Block läuft nur einmal.

  Zwei Sections binden diese Datei ein (Kopfleiste und Bühne), damit jede für
  sich funktioniert. Der Browser führt dann beide `<script src>` aus — und
  weil `const STUFEN` auf oberster Ebene stand, scheiterte der zweite Durchlauf
  mit „Identifier 'STUFEN' has already been declared". Auf der Startseite
  standen so drei unbehandelte SyntaxError in der Konsole.

  Kaputt war deshalb nichts: Ein solcher Fehler kippt den ganzen zweiten
  Durchlauf, bevor er etwas tut. Aber echte Fehler gehen zwischen dem Lärm
  unter, und wer die Konsole zum Suchen benutzt, sucht zuerst hier.

  Ein Block statt einer IIFE, damit die Einrückung der 1'000 Zeilen darunter
  unverändert bleibt: `const` und `class` sind blockweit, eine zweite
  Ausführung findet den Block gar nicht erst.
*/
if (!window.__vantiqFahrzeugsuche) {
window.__vantiqFahrzeugsuche = true;

/* Die Texte kommen aus `snippets/vantiq-texte.liquid`, das sie aus den
   Sprachdateien rendert. Diese Datei ist ein Asset und läuft nie durch
   Liquid — deutscher Text darin bliebe in jeder Sprache deutsch. */
const T = (schluessel, werte) =>
  (window.vantiqText ? window.vantiqText(schluessel, werte) : schluessel);

const STUFEN = ['marke', 'modell', 'typ', 'karosserie', 'motor'];

/*
  Die Vorschlags-API findet viel, ordnet kurze Modellnamen aber zu schwach ein:
  Bei „Audi TT“ gewann jedes beliebige Audi-Modell, weil „TT“ auch mitten in
  „quattro“ vorkommt. Für Menschen sind ganze Wörter und die exakte
  Marke-Modell-Kombination entscheidend. Diese Normalisierung und Bewertung
  bleibt bewusst im Client, damit dieselbe Suche in allen eingebauten
  Suchfeldern sofort gleich funktioniert.
*/
function suchtextNormalisieren(wert) {
  return String(wert || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function vorschlagBewerten(eintrag, suchtext) {
  const frage = suchtextNormalisieren(suchtext);
  const name = suchtextNormalisieren(eintrag?.name);
  const nameWoerter = name.split(' ').filter(Boolean);
  const frageWoerter = frage.split(' ').filter(Boolean);
  if (!frage || !name) return 0;

  let punkte = 0;
  if (name === frage) punkte += 100000;
  else if (name.startsWith(`${frage} `)) punkte += 50000;

  const alleAlsWort = frageWoerter.every((wort) => nameWoerter.includes(wort));
  if (alleAlsWort) punkte += 20000;

  frageWoerter.forEach((wort, position) => {
    if (nameWoerter[position] === wort) punkte += 2500;
    if (nameWoerter.includes(wort)) punkte += 1200;
    else if (nameWoerter.some((nameWort) => nameWort.startsWith(wort))) punkte += 300;
    else if (name.includes(wort)) punkte += 30;
  });

  // Kürzere Namen sind bei gleichem Treffergrad meist die präzisere Antwort.
  return punkte - name.length;
}

/*
  Eingabe deuten. Die drei Wege überschneiden sich nicht:

    1OC253                Typenschein — Ziffer, zwei Buchstaben, drei Ziffern
    205/55 R16            Grösse in kanonischer Schreibweise
    205 55 16, 205-55-16  Grösse, wie sie sonst getippt wird
    VW Golf               alles Übrige

  Die Wertebereiche stammen aus den Metafeldern des Shops: Breite 125–355,
  Querschnitt 25–85, Zoll 12–24. Ohne sie wäre "1 2 3" eine Grösse.
*/
const MUSTER = {
  typenschein: /^[0-9][A-Z]{2}[0-9]{3}$/,
  groesse: /^(\d{3})\s*[\/\-\s]?\s*(\d{2})\s*[\/\-\s]?\s*(\d{2})$/,
  // Nur Breite und Querschnitt, der Felgendurchmesser fehlt
  unvollstaendig: /^(\d{3})\s*[\/\-\s]\s*(\d{2})$/,
  // Nur die Breite. Wer "205" tippt, hat eine Grösse angefangen und keine
  // Automarke gemeint — ohne diesen Fall landete die Eingabe in der
  // Fahrzeugsuche und kam als "nichts gefunden" zurück.
  nurBreite: /^(\d{3})$/,
  // Bauartkennung in allen Schreibweisen, die auf einer Flanke vorkommen:
  // R, ZR, RF, ZRF, ROF, RFT. Sie trennt Querschnitt und Zoll und trägt für
  // die Suche keine Bedeutung.
  bauart: /\s*Z?R(?:OF|FT|F)?\s*/i,
};

function eingabeDeuten(roh) {
  const text = (roh || '').trim();
  if (!text) return { art: 'leer' };

  const kompakt = text.replace(/\s+/g, '').toUpperCase();

  // "IVI" steht im Fahrzeugausweis anstelle einer Nummer, wenn das Fahrzeug
  // über ein eDatenblatt zugelassen wurde. Wer das abtippt, sucht keine Marke
  // namens IVI - er braucht die Auskunft, dass es keine Nummer gibt.
  if (kompakt === 'IVI') return { art: 'ivi' };

  if (MUSTER.typenschein.test(kompakt)) {
    return { art: 'typenschein', nummer: kompakt };
  }

  const ohneBauart = text.replace(MUSTER.bauart, ' ').trim();

  const treffer = ohneBauart.match(MUSTER.groesse);
  if (treffer) {
    const [, breite, querschnitt, zoll] = treffer.map(Number);
    if (breite >= 125 && breite <= 355 && querschnitt >= 25 && querschnitt <= 85 && zoll >= 12 && zoll <= 24) {
      return {
        art: 'groesse',
        breite,
        querschnitt,
        zoll,
        anzeige: `${breite}/${querschnitt} R${zoll}`,
      };
    }
  }

  // "205/55" ist erkennbar eine angefangene Grösse. Sie zur Fahrzeugsuche zu
  // schicken liefert nichts und sagt auch nicht warum.
  const angefangen = ohneBauart.match(MUSTER.unvollstaendig);
  if (angefangen) {
    const [, breite, querschnitt] = angefangen.map(Number);
    if (breite >= 125 && breite <= 355 && querschnitt >= 25 && querschnitt <= 85) {
      return { art: 'groesse_unvollstaendig', breite, querschnitt };
    }
  }

  const nurBreite = ohneBauart.match(MUSTER.nurBreite);
  if (nurBreite) {
    const breite = Number(nurBreite[1]);
    if (breite >= 125 && breite <= 355) {
      return { art: 'groesse_nur_breite', breite };
    }
  }

  return { art: 'fahrzeug', text };
}

class Quelle {
  constructor(basis) {
    this.basis = (basis || '').replace(/\/+$/, '');
    this.markenAnfrage = null;
  }

  get istDemo() {
    return this.basis === '';
  }

  async holen(pfad, parameter = {}) {
    if (this.istDemo) return Demo.antwort(pfad, parameter);

    const url = new URL(`${this.basis}/${pfad}`, window.location.origin);
    Object.entries(parameter).forEach(([schluessel, wert]) => {
      if (wert) url.searchParams.set(schluessel, wert);
    });

    const antwort = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!antwort.ok) throw new Error(`HTTP ${antwort.status}`);
    return antwort.json();
  }

  optionen(stufe, auswahl) {
    const pfade = {
      marke: 'marken',
      modell: 'modelle',
      typ: 'typen',
      karosserie: 'karosserien',
      motor: 'motoren',
    };
    return this.holen(pfade[stufe], auswahl);
  }

  fahrzeug(auswahl) {
    return this.holen('fahrzeug', auswahl);
  }

  // Freitextsuche für das eine Feld. Erwartet Einträge mit `name`, optional
  // `zusatz` für die zweite Zeile, und `auswahl` als Schlüssel zum Fahrzeug.
  async vorschlaege(text) {
    const volltext = String(text || '').trim();
    if (!volltext) return [];

    const basisAnfrage = this.holen('vorschlaege', { q: volltext }).catch(() => []);

    /*
      Bei einer erkannten Marke wird zusätzlich nur nach dem Modellrest gesucht.
      „Audi TT“ wird damit sowohl als Ganzes als auch als „TT“ abgefragt. Die
      zweite Abfrage liefert das gesuchte Modell, selbst wenn die Serverrangliste
      beim Volltext nach der Marke übersteuert. Mehrteilige Marken wie Land Rover
      werden aus der echten Markenliste erkannt und nicht am ersten Leerzeichen
      abgeschnitten.
    */
    if (!this.markenAnfrage) {
      this.markenAnfrage = this.optionen('marke', {}).catch(() => []);
    }
    const [basis, marken] = await Promise.all([basisAnfrage, this.markenAnfrage]);
    const normal = suchtextNormalisieren(volltext);
    const markenwerte = (marken || [])
      .map((eintrag) => (typeof eintrag === 'string'
        ? eintrag
        : eintrag?.value || eintrag?.name || eintrag?.label || ''))
      .filter(Boolean)
      .sort((a, b) => suchtextNormalisieren(b).length - suchtextNormalisieren(a).length);
    const marke = markenwerte.find((wert) => {
      const m = suchtextNormalisieren(wert);
      return normal === m || normal.startsWith(`${m} `);
    });
    const modelltext = marke
      ? normal.slice(suchtextNormalisieren(marke).length).trim()
      : '';
    const modellTreffer = modelltext
      ? await this.holen('vorschlaege', { q: modelltext }).catch(() => [])
      : [];

    const gesehen = new Set();
    return [...basis, ...modellTreffer]
      .filter((eintrag) => {
        const auswahl = eintrag?.auswahl || {};
        const schluessel = [
          auswahl.marke,
          auswahl.modell,
          auswahl.typ,
          auswahl.karosserie,
          auswahl.motor,
          eintrag?.name,
          eintrag?.zusatz,
        ].join('|');
        if (gesehen.has(schluessel)) return false;
        gesehen.add(schluessel);
        return true;
      })
      .sort((a, b) => vorschlagBewerten(b, volltext) - vorschlagBewerten(a, volltext));
  }

  typenschein(nummer) {
    return this.holen(`typenschein/${encodeURIComponent(nummer)}`);
  }
}

class TireVehicleSearch extends HTMLElement {
  connectedCallback() {
    this.quelle = new Quelle(this.dataset.endpoint);
    /* Der Rückfall baut die Adresse aus `Shopify.routes.root`, damit sie in
       der französischen Ansicht nicht ins Deutsche zurückführt. Die
       gepflegte `resultsUrl` bringt das Präfix aus Liquid schon mit. */
    this.zielAdresse =
      this.dataset.resultsUrl
      || window.routes?.tire_collection_url
      || `${window.Shopify?.routes?.root || '/'}collections/reifen`.replace(/\/{2,}/g, '/');
    this.auswahl = {};
    this.vorschlagAnfrage = 0;
    this.vorschlagTimer = null;

    this.felder = STUFEN.map((stufe) => this.querySelector(`[data-step="${stufe}"]`));
    this.knopfFahrzeug = this.querySelector('[data-action="search-vehicle"]');
    this.eingabe = this.querySelector('[data-field="query"]');
    this.knopfSuchen = this.querySelector('[data-action="search-query"]');
    this.vorschlaege = this.querySelector('.tire-search__suggestions');
    this.kombi = this.querySelector('[role="combobox"]');
    this.schrittweise = this.querySelector('[data-role="stepwise"]');
    this.meldung = this.querySelector('[data-role="message"]');
    this.ergebnis = this.querySelector('[data-role="result"]');

    this.ergebnis.addEventListener('click', (ereignis) => {
      const reset = ereignis.target.closest('[data-action="manual-vehicle-reset"]');
      if (reset) this.manuelleAuswahlStarten();
    });

    // **Die Fahrzeugauswahl ist optional.** Wird das Formular mit
    // `stepwise: false` gerendert — die Buehne der Startseite tut das —, fehlen
    // die Auswahlfelder, der Fahrzeugknopf und der Aufklapper. Ohne diese
    // Pruefungen warf `connectedCallback` an dieser Stelle einen Fehler und
    // brach ab, BEVOR der Suchknopf verdrahtet war: Ein Klick auf „Suchen" tat
    // dann gar nichts, ohne sichtbare Ursache (15.08.2026).
    this.felder.forEach((feld) => {
      if (!feld) return;
      feld.addEventListener('change', () => this.stufeGewaehlt(feld));
    });
    if (this.knopfFahrzeug) {
      this.knopfFahrzeug.addEventListener('click', () => this.fahrzeugSuchen());
    }
    this.knopfSuchen.addEventListener('click', () => this.eingabeAbschicken());

    this.eingabe.addEventListener('input', () => this.eingabeGetippt());
    this.eingabe.addEventListener('keydown', (ereignis) => this.tasteImFeld(ereignis));
    this.eingabe.addEventListener('blur', () => {
      // Verzögert, sonst ist der angeklickte Vorschlag weg, bevor er zählt
      window.setTimeout(() => this.vorschlaegeSchliessen(), 120);
    });

    // Die Auswahlfelder erst laden, wenn jemand sie aufklappt: Wer die Grösse
    // kennt, braucht sie nie, und der erste Aufruf kostet sonst jedes Mal.
    if (this.schrittweise) {
      this.schrittweise.addEventListener('toggle', () => {
        if (this.schrittweise.open && !this.stufenGeladen) {
          this.stufenGeladen = true;
          this.stufeFuellen(0);
        }
      });
    }
  }

  // ---------------------------------------------------------------- Ein Feld

  eingabeGetippt() {
    const deutung = eingabeDeuten(this.eingabe.value);
    this.melden('');
    window.clearTimeout(this.vorschlagTimer);

    if (deutung.art === 'fahrzeug' && deutung.text.length >= 2) {
      // Erst nach einer kurzen Tipp-Pause suchen. Das spart sechs Anfragen für
      // „Audi TT“ und verhindert flackernde Zwischenresultate.
      this.vorschlagTimer = window.setTimeout(() => this.vorschlaegeZeigen(deutung.text), 180);
      return;
    }
    this.vorschlagAnfrage += 1;
    this.vorschlaegeSchliessen();
  }

  async vorschlaegeZeigen(text) {
    const anfrage = ++this.vorschlagAnfrage;
    let treffer = [];
    try {
      treffer = await this.quelle.vorschlaege(text);
    } catch (fehler) {
      treffer = [];
    }
    // Eine ältere, langsamere Antwort darf die neuere Eingabe nie ersetzen.
    if (anfrage !== this.vorschlagAnfrage || this.eingabe.value.trim() !== text.trim()) return;
    if (!treffer.length) {
      this.vorschlaegeSchliessen();
      return;
    }

    this.vorschlaege.innerHTML = treffer
      .slice(0, 8)
      .map(
        (eintrag, position) => `
          <li role="option" id="vorschlag-${position}" aria-selected="false"
              class="tire-search__suggestion" data-index="${position}">
            <span class="tire-search__suggestion-name">${this.sicher(eintrag.name)}</span>
            <span class="tire-search__suggestion-meta">${this.sicher(eintrag.zusatz || '')}</span>
          </li>`
      )
      .join('');
    this.trefferliste = treffer.slice(0, 8);
    this.markierung = -1;
    this.vorschlaege.hidden = false;
    this.kombi.setAttribute('aria-expanded', 'true');

    this.vorschlaege.querySelectorAll('[data-index]').forEach((zeile) => {
      zeile.addEventListener('mousedown', (ereignis) => {
        ereignis.preventDefault();
        this.vorschlagWaehlen(Number(zeile.dataset.index));
      });
    });
  }

  vorschlaegeSchliessen() {
    this.vorschlaege.hidden = true;
    this.vorschlaege.innerHTML = '';
    this.kombi.setAttribute('aria-expanded', 'false');
    this.markierung = -1;
  }

  tasteImFeld(ereignis) {
    if (ereignis.key === 'Enter') {
      ereignis.preventDefault();
      if (this.markierung >= 0) this.vorschlagWaehlen(this.markierung);
      else this.eingabeAbschicken();
      return;
    }
    if (ereignis.key === 'Escape') {
      this.vorschlaegeSchliessen();
      return;
    }
    const richtung = { ArrowDown: 1, ArrowUp: -1 }[ereignis.key];
    if (!richtung || this.vorschlaege.hidden) return;

    ereignis.preventDefault();
    const anzahl = this.trefferliste.length;
    this.markierung = (this.markierung + richtung + anzahl + 1) % (anzahl + 1) - 1;
    this.vorschlaege.querySelectorAll('[data-index]').forEach((zeile, position) => {
      const aktiv = position === this.markierung;
      zeile.classList.toggle('is-active', aktiv);
      zeile.setAttribute('aria-selected', String(aktiv));
    });
  }

  async vorschlagWaehlen(position) {
    const eintrag = this.trefferliste[position];
    if (!eintrag) return;
    this.eingabe.value = eintrag.name;
    this.vorschlaegeSchliessen();
    await this.fahrzeugZeigen(eintrag.auswahl);
  }

  async eingabeAbschicken() {
    const deutung = eingabeDeuten(this.eingabe.value);
    this.vorschlaegeSchliessen();
    this.ergebnisLeeren();

    if (deutung.art === 'leer') {
      this.melden(T('search.hint_input'), 'fehler');
      return;
    }

    if (deutung.art === 'groesse') {
      window.location.href = this.grZiel(deutung);
      return;
    }

    if (deutung.art === 'groesse_unvollstaendig') {
      this.melden(
        T('search.hint_rim', {
          groesse: `${deutung.breite}/${deutung.querschnitt}`,
          beispiel: `${deutung.breite}/${deutung.querschnitt} R16`,
        }),
        'hinweis'
      );
      return;
    }

    if (deutung.art === 'groesse_nur_breite') {
      this.melden(
        T('search.hint_width', { breite: deutung.breite, beispiel: `${deutung.breite}/55 R16` }),
        'hinweis'
      );
      return;
    }

    if (deutung.art === 'ivi') {
      this.melden(
        T('search.no_type_number'),
        'hinweis'
      );
      if (this.schrittweise) this.schrittweise.open = true;
      return;
    }

    if (deutung.art === 'typenschein') {
      await this.nummerSuchen(deutung.nummer);
      return;
    }

    // Freitext ohne gewählten Vorschlag: den ersten Treffer nehmen, sonst
    // sagen, dass nichts passt — kommentarlos nichts zu tun ist das Ärgerlichste.
    const treffer = await this.quelle.vorschlaege(deutung.text).catch(() => []);
    if (treffer.length) {
      await this.fahrzeugZeigen(treffer[0].auswahl);
      return;
    }
    this.melden(
      T('search.nothing_found', { text: deutung.text }),
      'fehler'
    );
  }

  /*
    Adresse zu einer Reifengrösse.

    Diese Komponente hat sich ihre Adressen selbst gebaut — mit den Parametern
    `breite`, `querschnitt` und `zoll` auf `/collections/reifen`. Die liest
    nirgends jemand: Die Kollektion zeigte daraufhin den GANZEN Katalog, und
    zwar ohne jedes Anzeichen, dass die Grösse verlorengegangen ist.

    Zuständig ist `assets/tire-groessensuche.js`. Es baut
    `/search?q=*&filter.p.m.custom.…`, weil Shopify die
    Storefront-Filter ab 5000 Produkten je Kollektion abschaltet und
    `?filter.…` dann stillschweigend ignoriert; die Suche kennt diese Grenze
    nicht. Genau dafür stellt es `window.vantiqGroesse.adresse` bereit — damit
    es nicht zwei leicht verschiedene Fassungen gibt. Der Rückfall unten ist
    wortgleich, für den Fall, dass das Skript auf einer Seite fehlt.
  */
  grAdresse(breite, quer, zoll, zusatz) {
    const g = { breite: breite, quer: quer, zoll: zoll };
    const tag = (zusatz && zusatz.tag) || this.dataset.groessentag || 'Reifen';

    let adresse;
    if (zusatz !== undefined) {
      // Fahrzeugfreigaben brauchen die Last-/Tempo-Filter. Auf grossen
      // Kollektionen ignoriert Shopify diese Filter; die Suche wertet sie aus.
      adresse =
        (window.routes?.search_url ||
          `${window.Shopify?.routes?.root || '/'}search`.replace(/\/{2,}/g, '/')) +
        '?type=product&q=' + encodeURIComponent(`tag:${g.breite}/${g.quer}/${g.zoll}`);
      if (tag === 'Sommerreifen' || tag === 'Winterreifen' || tag === 'Ganzjahresreifen') {
        adresse += `&filter.p.m.custom.season=${encodeURIComponent(tag)}`;
      }
    } else if (window.vantiqGroesse && window.vantiqGroesse.adresse) {
      adresse = window.vantiqGroesse.adresse(g, tag);
    } else {
      const saisonen = {
        Sommerreifen: 'Sommerreifen',
        Winterreifen: 'Winterreifen',
        Ganzjahresreifen: 'Ganzjahresreifen',
      };
      const saison = saisonen[tag] || '';
      adresse =
        (window.routes?.search_url ||
          `${window.Shopify?.routes?.root || '/'}search`.replace(/\/{2,}/g, '/')) +
        '?type=product&q=' + encodeURIComponent('*') +
        `&filter.p.m.custom.tire_width=${g.breite}` +
        `&filter.p.m.custom.aspect_ratio=${g.quer}` +
        `&filter.p.m.custom.rim_diameter=${g.zoll}`;
      if (saison) adresse += `&filter.p.m.custom.season=${encodeURIComponent(saison)}`;
    }

    /*
      Last- und Geschwindigkeitsindex AB dem geforderten Wert, nicht genau
      darauf. Ein höherer Index ist zulässig: Wer 95 V braucht, darf 95 W und
      99 Y fahren. Ein Filter auf „genau 95 V" blendet erlaubte Reifen aus —
      das wäre eine falsche Aussage über die Zulassung.

      Der Filter kennt kein „ab", er kennt nur Werte. Also werden alle Stufen
      vom geforderten Wert aufwärts angehängt; mehrere Werte derselben Gruppe
      wirken als Oder.

      Die Reihenfolge der Geschwindigkeitsbuchstaben ist nach km/h sortiert,
      nicht alphabetisch: H (210) liegt über T (190), obwohl es davor stünde.
    */
    const TEMPO = ['Q', 'R', 'S', 'T', 'U', 'H', 'V', 'W', 'Y'];

    if (zusatz && zusatz.last) {
      const ab = Number(zusatz.last);
      if (ab > 0) {
        for (let i = ab; i <= 130; i += 1) {
          adresse += `&filter.p.m.custom.load_index=${i}`;
        }
      }
    }
    if (zusatz && zusatz.tempo) {
      const stelle = TEMPO.indexOf(String(zusatz.tempo).toUpperCase());
      if (stelle !== -1) {
        TEMPO.slice(stelle).forEach((b) => {
          adresse += `&filter.p.m.custom.speed_index=${b}`;
        });
      }
    }
    return adresse;
  }

  grZiel(deutung) {
    return this.grAdresse(deutung.breite, deutung.querschnitt, deutung.zoll);
  }

  /*
    Adresse fuer den Knopf unter dem erkannten Fahrzeug.

    Fuehrt auf die HAUPTGROESSE — den ersten Satz ohne Winterhinweis —, nicht
    auf alle Groessen zusammen.

    Zusammen ginge nicht richtig: Die zulaessigen Indizes unterscheiden sich je
    Groesse. Beim Audi S4 verlangt 235/40 R18 den Index 95 Y, 225/45 R17 nur
    94 H. Eine gemeinsame Abfrage muss die Werte vereinigen und laesst damit
    H-Reifen in 18 Zoll durch, die fuer dieses Fahrzeug nicht zulaessig sind —
    aus 238 zulaessigen Reifen wurden 424 angezeigte. In einer Shopify-Abfrage
    laesst sich „je Groesse ein eigener Index" nicht ausdruecken.

    Die uebrigen Groessen stehen danach in der Fahrzeugkarte, jede mit ihrer
    eigenen exakten Abfrage.
  */
  hauptgroesseAdresse(fahrzeug) {
    const saetze = (fahrzeug.saetze || []);
    const ohneWinter = saetze.filter((s) => !/m\s*[+&]?\s*s|winter/i.test(String(s.hinweis || '')));
    const satz = ohneWinter[0] || saetze[0];
    if (!satz) return '';

    const achse = (satz.achsen || [])[0];
    if (!achse) return '';
    const m = /^(\d{2,3})\/(\d{2})\s*[A-Z]*(\d{2})$/.exec(String(achse.filter || '').trim());
    if (!m) return '';

    const idx = /\b(\d{2,3})\s*\(?([A-Z])\)?\s*$/.exec(String(achse.groesse || '').trim());
    const zusatz = idx ? { last: idx[1], tempo: idx[2] } : {};
    return this.grAdresse(m[1], m[2], m[3], zusatz);
  }

  async fahrzeugZeigen(auswahl) {
    try {
      const fahrzeug = await this.quelle.fahrzeug(auswahl);
      if (!fahrzeug) throw new Error('leer');
      this.ergebnisZeigen(fahrzeug);
      await this.kaskadeAngleichen(fahrzeug);
    } catch (fehler) {
      this.melden(T('search.no_tyre_data_vehicle'), 'fehler');
    }
  }

  /*
    Die Auswahlfelder auf ein gefundenes Fahrzeug nachziehen.

    Wer unten erst ein Fahrzeug zusammenklickt und sich dann doch für die
    Typenschein-Nummer entscheidet, sah zwei verschiedene Fahrzeuge
    gleichzeitig: unten das Ergebnis der Nummer, in der Mitte die alte
    Klickauswahl. Im gemeldeten Fall stand oben ein Audi Q7 in den Feldern,
    während die Nummer 1AC259 einen Audi S4 lieferte.

    Die Stufen hängen voneinander ab, also werden sie der Reihe nach geladen.
    Vorher wird ALLES geleert: Bricht das Nachziehen auf halber Strecke ab,
    weil ein Wert nicht in der Liste steht, ist ein leeres Feld ehrlich — ein
    stehengebliebenes behauptet etwas Falsches.

    Kein `ergebnisLeeren` und kein `stufeGewaehlt`: Beide würden das soeben
    gezeigte Ergebnis wieder wegräumen.
  */
  async kaskadeAngleichen(fahrzeug) {
    if (!fahrzeug) return;
    this.stufenGeladen = true;
    this.auswahl = {};

    this.felder.forEach((feld, position) => {
      this.optionenSetzen(feld, []);
      feld.value = '';
      feld.disabled = position > 0;
    });

    /*
      Marke und Modell stehen NICHT im Fahrzeug.

      Der Endpunkt liefert `name`, `typ`, `karosserie`, `motor`, `baujahre`,
      `typenschein` und `saetze` — mehr nicht. Die ersten beiden Stufen der
      Kaskade müssen deshalb aus `name` gewonnen werden ("Opel Astra").

      Verglichen wird gegen die Optionslisten selbst, nicht am Leerzeichen
      getrennt: "Addax Motors" und "Land Rover" sind zweiteilige Marken, und
      "3 Series Gran Turismo" ein vierteiliges Modell. Der längste passende
      Anfang gewinnt.
    */
    const anfang = (name, optionen) => {
      const kleine = String(name || '').toLowerCase();
      const treffer = optionen
        .map((o) => this.wertVon(o))
        .filter((wert) => wert && kleine.startsWith(String(wert).toLowerCase()));
      treffer.sort((a, b) => String(b).length - String(a).length);
      return treffer[0] || '';
    };

    const abgeleitet = {};
    if (!fahrzeug.marke || !fahrzeug.modell) {
      try {
        const marken = await this.quelle.optionen('marke', {});
        abgeleitet.marke = anfang(fahrzeug.name, marken);
        if (abgeleitet.marke) {
          const rest = String(fahrzeug.name).slice(abgeleitet.marke.length).trim();
          const modelle = await this.quelle.optionen('modell', { marke: abgeleitet.marke });
          abgeleitet.modell = anfang(rest, modelle);
        }
      } catch (fehler) {
        // Ohne die Listen bleibt die Kaskade leer — besser als falsch gefüllt.
      }
    }

    for (let position = 0; position < STUFEN.length; position += 1) {
      const stufe = STUFEN[position];
      const feld = this.felder[position];
      const wunsch = fahrzeug[stufe] || abgeleitet[stufe];
      if (!wunsch) break;

      let optionen = [];
      try {
        optionen = await this.quelle.optionen(stufe, this.auswahl);
      } catch (fehler) {
        break;
      }
      this.optionenSetzen(feld, optionen);
      feld.disabled = optionen.length === 0;

      // Ein exakter Typenschein ist autoritativer als die bewusst reduzierte
      // schrittweise Liste. Falls der genaue Datensatz dort nicht vorkommt,
      // bleibt sein Wert trotzdem sichtbar und auswählbar. So zeigt der Dialog
      // nach 1AC259 nicht bloss unten „Audi S4“, während oben leere Felder stehen.
      if (![...feld.options].some((option) => option.value === String(wunsch))) {
        const beschriftung = stufe === 'typ' && fahrzeug.baujahre
          ? T('search.years_type', { jahre: fahrzeug.baujahre, typ: wunsch })
          : String(wunsch);
        feld.append(new Option(beschriftung, String(wunsch)));
      }
      feld.disabled = false;
      feld.value = wunsch;
      // Zuweisen heisst bei <select> nicht übernehmen: Steht der Wert nicht
      // in der Liste, bleibt das Feld leer. Genau daran erkennt man es.
      if (feld.value !== String(wunsch)) break;
      this.auswahl[stufe] = feld.value;
    }

    this.knopfPruefen();
  }

  async manuelleAuswahlStarten() {
    this.eingabe.value = '';
    this.melden('');
    this.ergebnisLeeren();
    this.auswahl = {};
    if (this.schrittweise) this.schrittweise.open = true;
    this.stufenGeladen = true;
    this.felder.forEach((feld, position) => {
      this.optionenSetzen(feld, []);
      feld.value = '';
      feld.disabled = position > 0;
    });
    await this.stufeFuellen(0);
    this.felder[0].focus();
    this.knopfPruefen();
  }

  // ---------------------------------------------------------------- Kaskade

  async stufeFuellen(position) {
    const feld = this.felder[position];
    const stufe = STUFEN[position];

    this.ladeanzeige(feld, true);
    try {
      const optionen = await this.quelle.optionen(stufe, this.auswahl);
      this.optionenSetzen(feld, optionen);

      // Vor dem Selbstwählen freigeben: Ein Feld mit genau einem Eintrag ist
      // gefüllt, nicht gesperrt. Grau dargestellt läse es sich als "noch nicht
      // verfügbar", obwohl es fertig ist.
      feld.disabled = optionen.length === 0;

      // Eine einzige Möglichkeit ist keine Wahl. Sie automatisch zu setzen
      // spart einen Klick und überspringt die Karosserie dort, wo es nur eine
      // Bauform gibt — genau wie gefordert.
      if (optionen.length === 1) {
        feld.value = this.wertVon(optionen[0]);
        await this.stufeGewaehlt(feld);
      }
    } catch (fehler) {
      this.melden(T('search.unreachable'), 'fehler');
    } finally {
      this.ladeanzeige(feld, false);
    }
  }

  async stufeGewaehlt(feld) {
    const position = Number(feld.dataset.position);
    const stufe = STUFEN[position];

    // Alles hinter der geänderten Stufe verliert seine Gültigkeit.
    STUFEN.slice(position + 1).forEach((spaeter, versatz) => {
      delete this.auswahl[spaeter];
      const spaeteresFeld = this.felder[position + 1 + versatz];
      this.optionenSetzen(spaeteresFeld, []);
      spaeteresFeld.disabled = true;
    });
    this.ergebnisLeeren();

    if (!feld.value) {
      delete this.auswahl[stufe];
      this.knopfPruefen();
      return;
    }

    this.auswahl[stufe] = feld.value;
    if (position + 1 < STUFEN.length) await this.stufeFuellen(position + 1);
    this.knopfPruefen();
  }

  knopfPruefen() {
    this.knopfFahrzeug.disabled = !this.auswahl.motor;
    /*
      Die eine Stelle, an der sich die Vollständigkeit der Kaskade ändert — also
      die Stelle, die sie meldet.

      Der Knopf „Fahrzeug übernehmen" im Dialogfuss braucht diesen Zustand, darf
      ihn aber nicht am DOM ablesen: Der change-Handler oben ist async und setzt
      knopfFahrzeug.disabled bei den mittleren Stufen erst nach einem await.
      Ein Mithörer auf change läse dort noch den alten Wert und hielte den
      Übernehmen-Knopf offen, obwohl die Auswahl gerade ungültig geworden ist.
    */
    this.dispatchEvent(
      new CustomEvent('fahrzeug:bereit', {
        bubbles: true,
        detail: { bereit: !this.knopfFahrzeug.disabled },
      })
    );
  }

  optionenSetzen(feld, optionen) {
    const platzhalter = feld.dataset.step === 'marke' ? T('search.choose') : '—';
    feld.innerHTML = '';
    feld.append(new Option(platzhalter, ''));
    optionen.forEach((eintrag) => {
      feld.append(new Option(this.textVon(eintrag), this.wertVon(eintrag)));
    });
  }

  wertVon(eintrag) {
    return typeof eintrag === 'string' ? eintrag : eintrag.wert;
  }

  textVon(eintrag) {
    const text = typeof eintrag === 'string' ? eintrag : eintrag.text || eintrag.wert;
    return /^(?:schrägheck|hatchback)$/i.test(String(text)) ? T('search.hatchback') : text;
  }

  // Waehrend des Ladens gesperrt, danach entscheidet der Aufrufer anhand der
  // Anzahl Optionen. Hier nichts wieder freigeben - sonst waere ein Feld ohne
  // Treffer kurz bedienbar.
  ladeanzeige(feld, laeuft) {
    const anzeige = feld.parentElement.querySelector('.tire-search__spinner');
    if (anzeige) anzeige.hidden = !laeuft;
    if (laeuft) feld.disabled = true;
  }

  // ---------------------------------------------------------------- Suchen

  async fahrzeugSuchen() {
    this.melden('');
    try {
      const fahrzeug = await this.quelle.fahrzeug(this.auswahl);
      this.ergebnisZeigen(fahrzeug);
    } catch (fehler) {
      this.melden(T('search.no_tyre_data_choice'), 'fehler');
    }
  }

  async nummerSuchen(nummer) {
    // "IVI" steht im Fahrzeugausweis, wenn das Fahrzeug über ein eDatenblatt
    // zugelassen wurde. Es ist keine Nummer, sondern der Hinweis, dass es
    // keine gibt — deshalb eine eigene Antwort statt "nicht gefunden".
    if (nummer === 'IVI') {
      this.melden(
        T('search.no_type_number'),
        'hinweis'
      );
      if (this.schrittweise) this.schrittweise.open = true;
      return;
    }

    this.melden('');
    try {
      const fahrzeug = await this.quelle.typenschein(nummer);
      if (!fahrzeug) throw new Error('unbekannt');
      if (this.schrittweise) this.schrittweise.open = true;
      await this.kaskadeAngleichen(fahrzeug);
      this.ergebnisZeigen(fahrzeug, true);
    } catch (fehler) {
      this.melden(
        T('search.no_vehicle_for_number', { nummer }),
        'fehler'
      );
    }
  }

  // ---------------------------------------------------------------- Ausgabe

  melden(text, art = 'hinweis') {
    this.meldung.textContent = text;
    this.meldung.hidden = !text;
    this.meldung.className = `tire-search__message tire-search__message--${art}`;
  }

  ergebnisLeeren() {
    this.ergebnis.hidden = true;
    this.ergebnis.innerHTML = '';
    this.bekanntgeben(null);
  }

  /*
    Das gezeigte Fahrzeug nach außen melden.

    Notwendig, weil der Knopf „Fahrzeug übernehmen" nicht zu dieser Komponente
    gehört, sondern im Dialogfuß der Kopfleiste steht (sections/tire-header.liquid,
    verdrahtet in assets/startseite.js). Ohne dieses Ereignis hat er keinen
    Zugang zur Auswahl — er schrieb deshalb einen festen Beispielwert, und nach
    der Wahl eines BMW M2 stand im Kopf weiter „VW Golf VII 1.5 TSI".

    Das Ereignis steigt auf, damit der Zuhörer am document hängen kann und nicht
    an dieser Instanz: Die Suche liegt einmal auf der Startseite und einmal im
    Dialog.
  */
  bekanntgeben(fahrzeug) {
    this.fahrzeugAktuell = fahrzeug || null;
    this.dispatchEvent(
      new CustomEvent('fahrzeug:gezeigt', {
        bubbles: true,
        detail: this.fahrzeugAktuell,
      })
    );
  }

  ergebnisZeigen(fahrzeug, typenscheinTreffer = false) {
    const karosserie = fahrzeug.karosserie || '';
    let motor = fahrzeug.motor || '';
    const karosseriePraefix = `${karosserie} · `;
    if (karosserie && motor.startsWith(karosseriePraefix)) {
      motor = motor.slice(karosseriePraefix.length);
    }
    const kopfzeilen = [
      fahrzeug.typ ? T('search.type', { typ: fahrzeug.typ }) : '',
      karosserie,
      motor,
      fahrzeug.baujahre || '',
    ].filter(Boolean);

    const saetze = (fahrzeug.saetze || [])
      .map((satz) => this.satzMarkup(satz))
      .join('');

    this.ergebnis.innerHTML = `
      <div class="tire-search__vehicle${typenscheinTreffer ? ' tire-search__vehicle--erkannt' : ''}">
        ${typenscheinTreffer ? `
          <span class="tire-search__vehicle-haken" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12l5 5L20 6"/></svg>
          </span>` : ''}
        <div class="tire-search__vehicle-text">
          ${typenscheinTreffer ? `<p class="tire-search__result-kicker">${this.sicher(T('search.vehicle_recognised'))}</p>` : ''}
          <h3 class="tire-search__vehicle-name">${this.sicher(fahrzeug.name)}</h3>
          <p class="tire-search__vehicle-meta">${kopfzeilen.map((z) => this.sicher(z)).join(' · ')}</p>
        </div>
      </div>
      <div class="tire-search__sets">${saetze}</div>
      ${this.hauptgroesseAdresse(fahrzeug)
        ? `<button type="button" class="tire-search__all" data-action="alle-reifen"
             data-ziel="${this.sicher(this.hauptgroesseAdresse(fahrzeug))}">
             ${this.sicher(T('search.show_for_vehicle'))}
           </button>`
        : ''}
      ${typenscheinTreffer ? `
        <button type="button" class="tire-search__manual-reset" data-action="manual-vehicle-reset">
          ${this.sicher(T('search.choose_other_vehicle'))}
        </button>` : ''}
    `;
    const alleKnopf = this.ergebnis.querySelector('[data-action="alle-reifen"]');
    if (alleKnopf) {
      alleKnopf.addEventListener('click', () => {
        /* Das Fahrzeug wird mitgenommen, nicht nur die Liste geöffnet: Auf der
           Zielseite soll die Fahrzeugkarte stehen und die Zahlen je Grösse
           zeigen. Das Speichern erledigt startseite.js — dort liegt die einzige
           Stelle, die weiss, wie die drei Schlüssel zusammengehören. */
        document.dispatchEvent(new CustomEvent('vantiq:fahrzeug-uebernehmen', {
          detail: { fahrzeug: fahrzeug, ziel: alleKnopf.dataset.ziel }
        }));
      });
    }

    this.ergebnis.hidden = false;
    this.ergebnis.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    this.bekanntgeben(fahrzeug);
  }

  /*
    Ein Satz ist eine Einheit. Bei Mischbereifung stehen Vorder- und Hinterachse
    in derselben Karte, weil sie nur zusammen zulässig sind — als zwei Treffer
    dargestellt würde jemand vier gleiche Reifen kaufen.
  */
  satzMarkup(satz) {
    const zeilen = satz.achsen
      .map(
        (achse) => `
          <div class="tire-search__axle">
            ${achse.position ? `<span class="tire-search__axle-label">${this.sicher(achse.position)}</span>` : ''}
            <span class="tire-search__size">${this.sicher(achse.groesse)}</span>
          </div>`
      )
      .join('');

    /* Zeichen als rohes Markup, Text durch sicher() — der Hinweis kommt aus der
       Schnittstelle und darf kein HTML einschleppen.

       Der Hinweis steht ÜBER den Dimensionen: Er sagt, wofür dieser Satz gilt,
       und rahmt damit die Grössen darunter ein. Darunter gestellt las er sich
       wie eine Fussnote zur letzten Achse. */
    const teile = satz.hinweis ? this.hinweisTeile(satz.hinweis) : null;
    const merkmal = teile
      ? `<span class="tire-search__set-note${teile.klasse}">${teile.zeichen}${this.sicher(teile.text)}</span>`
      : '';

    /*
      Der Link führte auf `/collections/reifen?satz0=235/40R18` — und `satz0`
      wertet niemand aus. Wer auf „Passende Reifen anzeigen" klickte, bekam
      den ganzen Katalog zu sehen, nicht seine Grösse.

      Bei Mischbereifung gibt es zwei verschiedene Grössen, die sich nicht in
      EINE Liste bringen lassen: Der Filter kennt nur eine Breite. Deshalb
      bekommt jede Achse ihren eigenen Link. Vorne und hinten zusammen in
      einer Liste zu zeigen wäre auch sachlich falsch — man kauft zwei
      verschiedene Reifen, nicht vier gleiche.
    */
    const teileGroesse = (filter) => {
      const t = /^(\d{2,3})\/(\d{2})\s*[A-Z]*(\d{2})$/.exec(String(filter || '').trim());
      return t ? { breite: t[1], quer: t[2], zoll: t[3] } : null;
    };

    /*
      Saison und Indizes gehören mit in den Link.

      Der Link trug nur die Dimension. Auf einem Winterreifensatz führte er
      damit in eine Liste voller Sommerreifen, und Last- und
      Geschwindigkeitsindex fielen ganz weg — bei „235/40 R18 95 V" standen
      auch 91er darin, die das Fahrzeug nicht tragen dürfen.

      Die Saison kommt aus dem Hinweis des Satzes: „M+S" heisst Winter. Ohne
      Hinweis bleibt es beim Tag der Section, sonst behauptete der Link eine
      Saison, die in den Daten nicht steht.
    */
    const winter = /m\s*[+&]?\s*s|winter/i.test(String(satz.hinweis || ''));
    const zusatzBasis = winter ? { tag: 'Winterreifen' } : {};

    const links = (satz.achsen || [])
      .map((achse) => {
        const g = teileGroesse(achse.filter);
        if (!g) return '';

        // „235/40 R18 95 V" -> Last 95, Tempo V. Beides steht nur in der
        // Anzeigeform, nicht im Filterwert.
        const idx = /\b(\d{2,3})\s*\(?([A-Z])\)?\s*$/.exec(String(achse.groesse || '').trim());
        const zusatz = Object.assign({}, zusatzBasis,
          idx ? { last: idx[1], tempo: idx[2] } : {});
        const beschriftung =
          satz.achsen.length > 1 && achse.position
            ? T('search.show_tyres_axle', { position: this.sicher(achse.position) })
            : T('search.show_tyres');
        return `<a class="tire-search__set-link" href="${this.grAdresse(g.breite, g.quer, g.zoll, zusatz)}">${beschriftung}</a>`;
      })
      .join('');

    return `
      <article class="tire-search__set">
        <div class="tire-search__set-body">${merkmal}${zeilen}</div>
        ${links}
      </article>
    `;
  }

  /*
    Codes von der Reifenflanke in Klartext übersetzen.

    „M+S" steht so im Fahrzeugausweis und in den ASTRA-Daten, ist aber kein Satz,
    den jemand liest — wer nicht weiss, dass es Matsch und Schnee heisst, hält es
    für eine Artikelnummer. Der Code entfällt deshalb ganz: Schneeflocke plus
    „Winterbereifung" sagt dasselbe, ohne Vorwissen zu verlangen. Wer den Code
    sucht, findet ihn auf der Flanke und im Ausweis, nicht in einer Liste.

    Das Zeichen steht in der Tabelle und wird nicht daraus geschlossen, dass ein
    Code übersetzt wurde: Hinweise wie „nur mit Sportfahrwerk" sind schon
    Klartext, gehen unverändert durch und dürfen keine Schneeflocke bekommen.

    Die Schneeflocke ist dieselbe wie in der Kategorienzeile der Kopfleiste und
    auf den Reifenkarten.
  */
  hinweisTeile(hinweis) {
    const FLOCKE =
      '<svg class="tire-search__set-note-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
      ' stroke-width="1.9" stroke-linecap="round" aria-hidden="true">' +
      '<path d="M12 21.2 L12 2.8 M4.03 16.6 L19.97 7.4 M19.97 16.6 L4.03 7.4 M12 6.66 L13.6 4.62 M12 6.66 L10.4 4.62 M7.38 9.33 L6.4 6.92 M7.38 9.33 L4.8 9.69 M7.38 14.67 L4.8 14.31 M7.38 14.67 L6.4 17.08 M12 17.34 L10.4 19.38 M12 17.34 L13.6 19.38 M16.62 14.67 L17.6 17.08 M16.62 14.67 L19.2 14.31 M16.62 9.33 L19.2 9.69 M16.62 9.33 L17.6 6.92"/></svg>';

    const WINTER = {
      text: 'Winterbereifung',
      zeichen: FLOCKE,
      klasse: ' tire-search__set-note--winter',
    };
    const TABELLE = { 'M+S': WINTER, 'M&S': WINTER, MS: WINTER };

    return TABELLE[String(hinweis).trim()] || { text: hinweis, zeichen: '', klasse: '' };
  }

  sicher(text) {
    const knoten = document.createElement('div');
    knoten.textContent = text == null ? '' : String(text);
    return knoten.innerHTML;
  }
}

/* ------------------------------------------------------------------------
   Demobestand. Ersetzt die Schnittstelle, solange keine Adresse hinterlegt
   ist. Die Werte stammen aus der ASTRA-Kernschicht, damit die Maske mit
   echten Grössen und Achszuordnungen geprüft werden kann und nicht mit
   ausgedachten.
   ------------------------------------------------------------------------ */
const Demo = {
  fahrzeuge: [
    {
      marke: 'VW',
      modell: 'Golf',
      typ: '5G',
      karosserie: 'Kombi',
      motor: '2.0 TDI – 110 kW / 150 PS – DFGA',
      name: 'VW Golf Variant',
      baujahre: '2013–2020',
      saetze: [
        { achsen: [{ groesse: '205/55 R16 91 V', filter: '205/55R16' }] },
        { achsen: [{ groesse: '225/45 R17 91 W', filter: '225/45R17' }] },
        { achsen: [{ groesse: '225/40 R18 92 Y XL', filter: '225/40R18' }], hinweis: 'nur mit Sportfahrwerk' },
      ],
    },
    {
      marke: 'VW',
      modell: 'Golf',
      typ: '5G',
      karosserie: 'Schrägheck',
      motor: '1.4 TSI – 92 kW / 125 PS – CZCA',
      name: 'VW Golf',
      baujahre: '2012–2019',
      saetze: [
        { achsen: [{ groesse: '195/65 R15 91 H', filter: '195/65R15' }] },
        { achsen: [{ groesse: '205/55 R16 91 V', filter: '205/55R16' }] },
        { achsen: [{ groesse: '225/45 R17 91 W', filter: '225/45R17' }] },
      ],
    },
    {
      marke: 'BMW',
      modell: 'M2',
      typ: 'M3',
      karosserie: 'Coupé',
      motor: '3.0 – 272 kW / 370 PS – N55B30A',
      name: 'BMW M2',
      baujahre: '2015–2018',
      saetze: [
        {
          achsen: [
            { position: 'Vorderachse', groesse: '245/35 R19 93 Y', filter: '245/35R19' },
            { position: 'Hinterachse', groesse: '265/35 R19 98 Y', filter: '265/35R19' },
          ],
        },
        {
          achsen: [
            { position: 'Vorderachse', groesse: '235/40 R18 95 V', filter: '235/40R18' },
            { position: 'Hinterachse', groesse: '235/40 R18 95 V', filter: '235/40R18' },
          ],
          hinweis: 'M+S',
        },
        {
          achsen: [
            { position: 'Vorderachse', groesse: '235/40 R18 95 V', filter: '235/40R18' },
            { position: 'Hinterachse', groesse: '255/35 R18 94 V', filter: '255/35R18' },
          ],
          hinweis: 'M+S',
        },
      ],
    },
    {
      marke: 'Opel',
      modell: 'Astra',
      typ: 'P-J',
      karosserie: 'Schrägheck',
      motor: '1.6 – 85 kW / 115 PS – 16SHL',
      name: 'Opel Astra J 16SHL',
      baujahre: '2015',
      typenschein: '1OC253',
      saetze: [
        { achsen: [{ groesse: '215/60 R16 95 V', filter: '215/60R16' }] },
        { achsen: [{ groesse: '225/50 R17 94 V', filter: '225/50R17' }] },
        { achsen: [{ groesse: '235/45 R18 94 W', filter: '235/45R18' }] },
        { achsen: [{ groesse: '235/40 R19 92 W', filter: '235/40R19' }] },
      ],
    },
  ],

  antwort(pfad, parameter) {
    if (pfad.startsWith('typenschein/')) {
      const nummer = decodeURIComponent(pfad.split('/')[1]);
      return this.fahrzeuge.find((f) => f.typenschein === nummer) || null;
    }

    if (pfad === 'vorschlaege') {
      const suche = (parameter.q || '').toLowerCase();
      return this.fahrzeuge
        .filter((fahrzeug) =>
          `${fahrzeug.marke} ${fahrzeug.modell} ${fahrzeug.name}`.toLowerCase().includes(suche)
        )
        .map((fahrzeug) => ({
          name: fahrzeug.name,
          zusatz: [fahrzeug.karosserie, fahrzeug.motor, fahrzeug.baujahre].filter(Boolean).join(' · '),
          auswahl: {
            marke: fahrzeug.marke,
            modell: fahrzeug.modell,
            typ: fahrzeug.typ,
            karosserie: fahrzeug.karosserie,
            motor: fahrzeug.motor,
          },
        }));
    }

    const passend = this.fahrzeuge.filter((fahrzeug) =>
      Object.entries(parameter).every(([schluessel, wert]) => !wert || fahrzeug[schluessel] === wert)
    );

    if (pfad === 'fahrzeug') return passend[0] || null;

    // Der Typ trägt sein Baujahr im Text: der Code ist der Schlüssel, das
    // Baujahr ist das, wonach der Kunde sein Auto erkennt.
    //
    // Zeiträume werden zu einem zusammengezogen. Unter einem Typcode liegen
    // mehrere Ausführungen mit eigenen Zeiträumen — "2013–2020, 2012–2019"
    // nebeneinander zu stellen sähe nach zwei Autos aus, obwohl es eines ist.
    if (pfad === 'typen') {
      const gesammelt = new Map();
      passend.forEach((fahrzeug) => {
        if (!gesammelt.has(fahrzeug.typ)) gesammelt.set(fahrzeug.typ, []);
        (String(fahrzeug.baujahre || '').match(/\d{4}/g) || []).forEach((jahr) =>
          gesammelt.get(fahrzeug.typ).push(Number(jahr))
        );
      });

      return Array.from(gesammelt, ([typ, jahre]) => {
        if (!jahre.length) return { wert: typ, text: T('search.type', { typ }) };
        const von = Math.min(...jahre);
        const bis = Math.max(...jahre);
        const zeitraum = von === bis ? `${von}` : `${von}–${bis}`;
        return { wert: typ, text: `${zeitraum} · ${T('search.type', { typ })}` };
      });
    }

    const feld = {
      marken: 'marke',
      modelle: 'modell',
      karosserien: 'karosserie',
      motoren: 'motor',
    }[pfad];

    return Array.from(new Set(passend.map((fahrzeug) => fahrzeug[feld]))).sort();
  },
};

/*
  Erst ganz am Schluss registrieren. Die Registrierung stuft vorhandene
  Elemente sofort hoch, connectedCallback laeuft also noch waehrend dieser
  Datei — und wuerde weiter oben auf ein Demo zugreifen, das es zu dem
  Zeitpunkt noch nicht gibt.
*/
customElements.define('tire-vehicle-search', TireVehicleSearch);

} /* Ende der Einmal-Sperre — siehe Kommentar bei window.__vantiqFahrzeugsuche */
